import * as ko from 'knockout';
import page from 'page';
import i18n from '../i18n';

import { ListRequestParams } from '../api/request';
import { ListLoader, ListLoaderDelegate, delegateActions } from '../components/list_loader';
import { User, ftUserOptions } from '../models/user';
import * as usersApi from '../api/users';
import { countriesApi } from '../api/countries';
import { Action } from '../components/basic_widgets';
import { CountryData } from '../api/countries';
import { deflateList } from '../api/serialization';
import { session } from '../session';
import { FilterDelegate } from '../components/list_filters';
import { updateLocationWithQueryString, asArray, downloadBlob, timeAgo } from '../utils';
import { BaseLoadingScreen } from './base_loading_screen';
import { openResendInvite } from '../components/resend_invite';
import { ListHeaderAction } from '../components/list_header';
import {
  canChangeUsers,
  canInviteUsers,
  canRemoveUsers,
  customPermissionsEnabled,
  reactListTablesEnabled,
} from '../permissions';
import { GridView, pageParams } from '../utils/grid_view';
import { GridChoices, listLookup } from '../utils/grid_choices';
import {
  UsersListChoice,
  UsersListChoices,
  UsersListPage,
  UsersListProps,
  UsersListQuery,
  UsersListRow,
} from 'react-islands';

let template = require('raw-loader!../../templates/users.html').default;

function emptyChoices(): UsersListChoices {
  return { country: [], role: roleChoices() };
}

function roleChoices(): UsersListChoice[] {
  return ftUserOptions.roleOptions.map((role) => ({ value: role.id, label: role.name }));
}

class UsersScreen extends BaseLoadingScreen implements ListLoaderDelegate<usersApi.UserData, User> {
  loader: ListLoader<usersApi.UserData, User>;
  private countryFilter = ko.observableArray<CountryData>(null);
  private roleFilter = ko.observableArray<{ name: string; id: string }>();
  searchPlaceholder = i18n.t('Search users by name or email')();
  canEdit = canChangeUsers();
  canInvite = canInviteUsers();
  canDelete = canRemoveUsers();
  // The manage list is the one the row actions apply to, so ask for it whenever the actor holds
  // either of them; a read-only viewer asks for the plain roster instead of an empty manage list.
  forEdit = !customPermissionsEnabled() || canChangeUsers() || canRemoveUsers();
  exporting = ko.observable(false);
  newUsersTable = reactListTablesEnabled();

  newFilters: FilterDelegate[] = [
    {
      title: i18n.t('Country')(),
      entities: this.countryFilter,
      list: countriesApi.list,
    },
    {
      title: i18n.t('Role')(),
      entities: this.roleFilter,
      list: (params) => {
        let roles = ftUserOptions.roleOptions;
        if (params.name_prefix) {
          roles = roles.filter((role) => role.name.indexOf(params.name_prefix) >= 0);
        }
        return Promise.resolve(roles);
      },
    },
  ];
  listActions = ko.observableArray<ListHeaderAction>();

  private view = new GridView(
    'users',
    new GridChoices<UsersListChoices>(
      {
        country: listLookup(countriesApi.list, 'country'),
      },
      emptyChoices
    )
  );
  /** Shared with the legacy search box. */
  search = this.view.search;

  tableProps = ko.pureComputed<UsersListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
  }));

  constructor(params: {
    filters: { countries: string | string[]; roles: string | string[]; search: string };
  }) {
    super();

    this.addListActions();

    if (this.newUsersTable) {
      this.loadedAfter(this.view.load(params.filters));
      return;
    }

    let roles = asArray(params.filters.roles);
    this.roleFilter(ftUserOptions.roleOptions.filter((role) => roles.indexOf(role.id) !== -1));

    if (params.filters.search) {
      this.search(params.filters.search);
    }
    let countries = asArray(params.filters.countries);
    let countriesPromise =
      countries.length > 0 ? countriesApi.list({ ids: countries }) : Promise.resolve<CountryData[]>([]);
    let promise = countriesPromise.then((countryData) => {
      if (countryData) {
        this.countryFilter(countryData);
      }
    });

    this.loadedAfter(promise);
  }

  private addListActions() {
    if (this.canInvite) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/users/new/',
        tooltipTitle: i18n.t('Add User')(),
      });
    }
    this.listActions.push({
      title: i18n.t('Export')(),
      icon: 'file_download',
      onClick: this.export,
      loading: this.exporting,
    });
  }

  private exportParams(): Record<string, unknown> {
    if (this.newUsersTable) {
      return { ...this.view.currentQuery(), for_edit: this.forEdit };
    }
    return {
      roles: deflateList(this.roleFilter),
      countries: deflateList(this.countryFilter),
      search: this.search(),
      for_edit: this.forEdit,
    };
  }

  export = async () => {
    this.exporting(true);
    try {
      const data = await usersApi.exportUsers(this.exportParams());
      downloadBlob(data, 'users.xlsx');
    } finally {
      this.exporting(false);
    }
  };

  onReady(loader: ListLoader<usersApi.UserData, User>): void {
    this.loader = loader;
    this.search.subscribe(() => this.loader.forceLoad());
  }

  fetch(params: ListRequestParams) {
    let filters = {
      roles: deflateList(this.roleFilter),
      countries: deflateList(this.countryFilter),
      search: this.search(),
    };
    updateLocationWithQueryString(filters);

    return usersApi.list({ ...filters, ...params, for_edit: this.forEdit });
  }

  instantiate(userData: usersApi.UserData) {
    return new User(ftUserOptions, userData);
  }

  remove(id: string) {
    return usersApi.remove(id);
  }

  canRemove(user: User) {
    return this.canDelete;
  }

  private fetchPage = (query: UsersListQuery): Promise<UsersListPage> =>
    this.view.fetchPage(
      query,
      (options) =>
        usersApi.list({ ...pageParams(query, 'search'), for_edit: this.forEdit }, undefined, options),
      this
    );

  toRow = (user: User): UsersListRow => {
    return {
      id: user.id(),
      name: user.name(),
      url: session.toTenantPath(user.editUrl()),
      email: user.email(),
      roleName: user.roleName() || '',
      countryName: user.country() ? user.country().name : '',
      dataEntryPermissionText: user.dataEntryPermissionsText || '',
      dataEntryPermissionIcon: user.dataEntryPermissionsIcon || '',
      lastSyncTime: user.lastSyncTime ? timeAgo(user.lastSyncTime) : '',
      lastSyncUploadTime: user.lastSyncUploadTime ? timeAgo(user.lastSyncUploadTime) : '',
      lastSyncOs: user.lastSyncOS || '',
      lastSyncAppVersion: user.lastSyncAppVersion || '',
      actions: delegateActions(this, user, this.view.reload),
    };
  };

  private makeRole(user: User, role: usersApi.UserRole) {
    usersApi.makeRole(user.id(), role).then(() => {
      user.role(role);
      // The Role column renders roleName, so it stays on the previous role until it is set too.
      const selectedOption = ftUserOptions.roleOptions.find((option) => option.id === role);
      if (selectedOption) {
        user.roleName(selectedOption.name);
      }
      this.view.reload();
    });
  }

  getActions(user: User): Action[] {
    let res: Action[] = [
      {
        icon: 'map',
        title: i18n.t('Map activities')(),
        cssClass: '',
        onClick: () => {
          page(session.toTenantPath('/users/' + user.id() + '/activities/'));
        },
      },
    ];

    if (this.canEdit) {
      res.push({
        icon: 'send',
        title: i18n.t('Resend invite')(),
        cssClass: '',
        onClick: () => openResendInvite(user),
      });
    }

    if (session.isAdmin() && !customPermissionsEnabled()) {
      if (user.role() !== 'admin') {
        res.push({
          icon: 'lock',
          title: i18n.t('Make admin')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'admin');
          },
        });
      }

      if (user.role() !== 'read_only_admin') {
        res.push({
          icon: 'lock',
          title: i18n.t('Make read-only admin')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'read_only_admin');
          },
        });
      }

      if (user.role() !== 'template_editor') {
        res.push({
          icon: 'lock',
          title: i18n.t('Make template editor')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'template_editor');
          },
        });
      }

      if (user.role() !== 'manager') {
        res.push({
          icon: 'lock',
          title: i18n.t('Make manager')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'manager');
          },
        });
      }

      if (
        user.role() !== 'restricted_manager' &&
        session.tenant() &&
        session.tenant().restricted_manager_enabled
      ) {
        res.push({
          icon: 'lock',
          title: i18n.t('Make restricted manager')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'restricted_manager');
          },
        });
      }

      if (user.role() !== 'editor' && !session.isManager()) {
        res.push({
          icon: 'lock',
          title: i18n.t('Make editor')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'editor');
          },
        });
      }

      if (user.role() !== 'data_entry') {
        res.push({
          icon: 'lock_open',
          title: i18n.t('Make staff')(),
          cssClass: '',
          onClick: () => {
            this.makeRole(user, 'data_entry');
          },
        });
      }
    }

    return res;
  }
}

export let users = {
  name: 'users',
  viewModel: UsersScreen,
  template: template,
};

ko.components.register(users.name, users);
